import { normalizeName, parseAnchor } from "./anchor.js";
import { STAMPED_COLLECTIONS, type GltfElement, type GltfJson } from "./gltf.js";
import { randomUuid, uuidv5 } from "./uuid.js";
import type { GraphNode } from "./address.js";

/**
 * THE identity module: every read and every mint of a needle-id lives here —
 * derivation (deterministic ids for glTF elements), glTF stamping/readback
 * (`extras`), and live-node stamping/readback (`userData`). Consumers never
 * touch the key or mint ids themselves.
 */

/** The key carrying the persistent id — glTF `extras` for file elements, live
 * `userData` for scene objects (exports copy one into the other). */
export const NEEDLE_ID_KEY = "needle-id";

// #region Derivation

/** Stable instance identity, preserving the namespace used by Engine duplication. */
export function deriveInstanceId(scope: string, originalId: string): string {
    return uuidv5(`${scope}:${originalId}`, "eff8ba80-635d-11ec-90d6-0242ac120003");
}

/**
 * The name an id is derived FROM: the anchor marker removed (if any) and space/underscore
 * runs collapsed.
 *
 * The marker is stripped because **ids are minted as if no anchor existed** — an anchor is
 * a LABEL, not an identity, and the two must be able to change independently. Both halves
 * of that are load-bearing:
 * - stripping the marker means ADDING or REMOVING `#needle` leaves the id untouched (under
 *   the previous rule, anchoring an object CHANGED its id — so the very act recommended for
 *   making something stable orphaned every edit already addressed to it);
 * - {@link occurrenceOfName} groups on this same value, so anchoring ONE of two `Door`s
 *   does not shift the OTHER one's occurrence.
 *
 * Note what is decoupled: the id is independent of the anchor MARKER, not of the NAME.
 * Renaming `Door #needle` → `Entrance #needle` still changes a DERIVED id; a STAMPED id is
 * unaffected, since existing ids are never overwritten.
 */
export function idNameOf(name: string | null | undefined): string {
    if (typeof name !== "string") return "";
    // an anchor's value is already marker-stripped and normalized
    return parseAnchor(name)?.value ?? normalizeName(name);
}

/**
 * The deterministic needle-id of a glTF element:
 * `uuidv5("<type>-<idName>-<occurrence>[-<baseHash>]")`, where `idName` is the name with
 * any anchor marker removed ({@link idNameOf}) and `occurrence` is the element's position
 * among same-named elements of its type. Uniquely named elements are therefore fully
 * reorder-stable across file versions; duplicates shift only when reordered within their
 * own name group; unnamed elements are positional among the unnamed.
 *
 * `baseHash` is an optional consumer-supplied salt (e.g. an asset/content id) that scopes
 * ids to an external identity. Omitted by default, so any tool stamping the same element
 * mints the same id with no coordination.
 *
 * Ids are persisted in `extras["needle-id"]` and never overwritten once present —
 * whoever stamps first wins; this rule only governs never-stamped elements.
 */
export function computeElementId(type: string, name: string | null | undefined, occurrence: number, baseHash?: string): string {
    return uuidv5(`${type}-${idNameOf(name)}-${occurrence}${baseHash ? `-${baseHash}` : ""}`);
}

/**
 * The occurrence of the element at `index` among same-named elements of its collection —
 * the positional input of {@link computeElementId}. Exposed so consumers computing ids
 * for individual elements (instead of stamping whole files) use the identical rule.
 *
 * Grouping is by {@link idNameOf}, so an anchored element counts in the same group as its
 * unanchored namesakes.
 */
export function occurrenceOfName(elements: ReadonlyArray<{ name?: unknown } | null | undefined>, index: number): number {
    const name = idNameOf(elements[index]?.name as string | null | undefined);
    let occurrence = 0;
    for (let i = 0; i < index; i++) {
        if (idNameOf(elements[i]?.name as string | null | undefined) === name) occurrence++;
    }
    return occurrence;
}

// #endregion

// #region glTF elements (extras)

export type EnsureIdsOptions = {
    /**
     * Optional salt scoping unanchored ids to an external identity (e.g. an
     * asset/content id). By default no salt is used — ids derive from the element alone
     * (type, name, occurrence-of-name), so any tool mints the same ids with no
     * coordination. With a salt, only writers that know it agree — safe because
     * existing ids are never overwritten (whoever stamps first wins).
     */
    baseHash?: string;
};

export type EnsureIdsResult = {
    /** the salt the ids were derived with, when one was provided */
    baseHash?: string;
    /** how many elements received a new id (0 → the file was already fully stamped) */
    assigned: number;
};

/**
 * Idempotently inject `extras["needle-id"]` into every element of the stamped
 * collections. Existing ids are never overwritten, so already-stamped files pass
 * through unchanged and every id writer agrees with whoever stamped first.
 *
 * Mutates `json` in place.
 */
export function ensureIds(json: GltfJson, options?: EnsureIdsOptions): EnsureIdsResult {
    const baseHash = options?.baseHash;
    let assigned = 0;
    for (const [type, collection] of STAMPED_COLLECTIONS) {
        const elements = json[collection];
        if (!Array.isArray(elements)) continue;
        // occurrence-of-name: the id input is the element's position among same-named
        // elements, NOT its collection index — uniquely named elements keep their ids
        // across reorders and asset updates. Grouping is by the DERIVATION name
        // (anchor marker stripped, normalized), matching occurrenceOfName.
        const occurrences = new Map<string, number>();
        for (let index = 0; index < elements.length; index++) {
            const element = elements[index] as GltfElement;
            if (element == null || typeof element !== "object") continue;
            const name = typeof element.name === "string" ? element.name : "";
            const group = idNameOf(name);
            const occurrence = occurrences.get(group) ?? 0;
            occurrences.set(group, occurrence + 1);
            element.extras ??= {};
            if (element.extras[NEEDLE_ID_KEY] === undefined) {
                element.extras[NEEDLE_ID_KEY] = computeElementId(type, name, occurrence, baseHash);
                assigned++;
            }
        }
    }
    return { baseHash, assigned };
}

/** Read an element's needle-id without computing anything. */
export function getElementId(element: GltfElement | null | undefined): string | null {
    const id = element?.extras?.[NEEDLE_ID_KEY];
    return typeof id === "string" ? id : null;
}

// #endregion

// #region Live nodes (userData)

/** Read a live node's needle-id from `userData` — the live-object counterpart of
 * {@link getElementId}, which reads glTF `extras`. */
export function getNodeId(target: GraphNode | null | undefined): string | null {
    const id = target?.userData?.[NEEDLE_ID_KEY];
    return typeof id === "string" ? id : null;
}

/** Ensure a live node carries a needle-id: returns the existing one or MINTS a
 * random uuid into `userData`. Random by design — editor-created (scratch) content
 * has no derivation basis; the id persists because exports write `userData` ids
 * into glTF `extras` (create → save → reload sees the same id). */
export function ensureNodeId(target: GraphNode): string {
    const userData = (target.userData ??= {});
    const existing = userData[NEEDLE_ID_KEY];
    if (typeof existing === "string") return existing;
    const id = randomUuid();
    userData[NEEDLE_ID_KEY] = id;
    return id;
}

export type ShedIdentityOptions = {
    /** additional identity keys deleted from every `userData.components` entry —
     * the caller names its component-identity keys (e.g. an engine's editor-guid
     * key); addressing owns the traversal and the needle-id rule */
    componentKeys?: readonly string[];
};

/**
 * Shed a subtree's AUTHORED identity — for clones (duplicate/paste) before they are
 * re-stamped. Stamped needle-ids are deleted on every node: sibling id collisions
 * break addressing (an id-addressed edit resolves to whichever holder comes first).
 * With `componentKeys`, the same keys are deleted from `userData.components`
 * entries (a copied component guid would bake the ORIGINAL's identity into the
 * clone's next export). Fresh ids are minted by whatever stamps next.
 */
export function shedNodeIdentity(root: GraphNode, options?: ShedIdentityOptions): void {
    const componentKeys = options?.componentKeys ?? [];
    const visit = (node: GraphNode): void => {
        const userData = node.userData;
        if (userData) {
            if (typeof userData[NEEDLE_ID_KEY] === "string") delete userData[NEEDLE_ID_KEY];
            const components = userData["components"];
            if (Array.isArray(components)) {
                for (const component of components) {
                    if (!component || typeof component !== "object") continue;
                    for (const key of componentKeys) {
                        if ((component as Record<string, unknown>)[key] !== undefined) delete (component as Record<string, unknown>)[key];
                    }
                }
            }
        }
        for (const child of node.children ?? []) visit(child);
    };
    visit(root);
}

// #endregion
